import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES, safeReturnPath } from '../../../constants/routes';
import axios from 'axios';

function CreateRequest() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = safeReturnPath(searchParams.get('returnTo'));
  const [formData, setFormData] = useState({
    property: '',
    status: 'open',
    statusId: '',
    requestType: '',
    requestTypeId: '',
    location: '',
    locationId: '',
    requester: user?.id || '',
    date: new Date().toISOString().split('T')[0],
    permission: false,
    occupied: false,
    priority: 'Low',
    tech: '',
    problem: '',
    notes: '',
    timeSpent: '',
    dateFinished: '',
    followUp: ''
  });
  
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Data states
  const [properties, setProperties] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [techs, setTechs] = useState([]);

  // Filtered options based on selected property
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [filteredRequestTypes, setFilteredRequestTypes] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [filteredRequesters, setFilteredRequesters] = useState([]);
  const [filteredTechs, setFilteredTechs] = useState([]);

  const priorityOptions = ['Low', 'Medium', 'High'];
  const prevPropertyRef = useRef(undefined);

  // Fetch all data on component mount
  useEffect(() => {
    fetchProperties();
    fetchAllData();
  }, []);

  useEffect(() => {
    const pid = searchParams.get('propertyId');
    const problem = searchParams.get('problem');
    if (!pid && !problem) return;
    setFormData((prev) => ({
      ...prev,
      ...(pid ? { property: String(pid) } : {}),
      ...(problem ? { problem: decodeURIComponent(problem) } : {}),
    }));
  }, [searchParams]);

  // Filter options when property or lookup data changes (re-run after fetch completes)
  useEffect(() => {
    const pid = formData.property;
    if (!pid) {
      setFilteredStatuses([]);
      setFilteredRequestTypes([]);
      setFilteredLocations([]);
      setFilteredRequesters([]);
      setFilteredTechs([]);
      prevPropertyRef.current = pid;
      return;
    }

    const propertyChanged =
      prevPropertyRef.current !== undefined &&
      String(prevPropertyRef.current) !== String(pid);
    prevPropertyRef.current = pid;

    if (propertyChanged) {
      setFormData((prev) => ({
        ...prev,
        status: 'open',
        statusId: '',
        requestType: '',
        requestTypeId: '',
        location: '',
        locationId: '',
        requester: user?.id || '',
        tech: '',
      }));
    }

    const hasLookupData =
      statuses.length > 0 ||
      requestTypes.length > 0 ||
      locations.length > 0 ||
      requesters.length > 0 ||
      techs.length > 0;
    if (hasLookupData) {
      filterOptionsByProperty(pid);
    }
  }, [formData.property, statuses, requestTypes, locations, requesters, techs, user?.id]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProperties(response.data);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      // Fallback data
      setProperties([
        { id: 1, name: 'Building A', address: '123 Main St' },
        { id: 2, name: 'Building B', address: '456 Oak Ave' },
        { id: 3, name: 'Building C', address: '789 Pine Rd' }
      ]);
    }
  };

  const fetchAllData = async () => {
    try {
      // Fetch statuses
      const statusResponse = await axios.get('/api/statuses', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStatuses(statusResponse.data);

      // Fetch request types
      const requestTypesResponse = await axios.get('/api/request-types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequestTypes(requestTypesResponse.data);

      // Fetch locations
      const locationsResponse = await axios.get('/api/locations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLocations(locationsResponse.data);

      // Fetch requesters
      const requestersResponse = await axios.get('/api/requesters', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequesters(requestersResponse.data);

      // Fetch assigned technicians
      const assignedResponse = await axios.get('/api/assigned', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTechs(assignedResponse.data);

    } catch (err) {
      console.error('Failed to fetch data:', err);
      // Fallback to mock data if API calls fail
      setRequestTypes([
        { id: 1, name: 'Maintenance', property_id: 1 },
        { id: 2, name: 'Repair', property_id: 1 },
        { id: 3, name: 'Installation', property_id: 2 },
        { id: 4, name: 'Inspection', property_id: 2 },
        { id: 5, name: 'Other', property_id: 3 }
      ]);

      setLocations([
        { id: 1, name: 'Kitchen', property_id: 1 },
        { id: 2, name: 'Bathroom', property_id: 1 },
        { id: 3, name: 'Living Room', property_id: 1 },
        { id: 4, name: 'Lobby', property_id: 2 },
        { id: 5, name: 'Office', property_id: 2 },
        { id: 6, name: 'Exterior', property_id: 3 }
      ]);

      setRequesters([
        { id: 1, name: 'John Smith', contact: 'john@example.com', property_id: 1 },
        { id: 2, name: 'Jane Doe', contact: 'jane@example.com', property_id: 1 },
        { id: 3, name: 'Bob Wilson', contact: 'bob@example.com', property_id: 2 },
        { id: 4, name: 'Alice Brown', contact: 'alice@example.com', property_id: 3 }
      ]);

      setTechs([
        { id: 1, technician: 'Tech 1', assignedRequests: 5, property_id: 1 },
        { id: 2, technician: 'Tech 2', assignedRequests: 3, property_id: 1 },
        { id: 3, technician: 'Tech 3', assignedRequests: 2, property_id: 2 },
        { id: 4, technician: 'Tech 4', assignedRequests: 4, property_id: 3 }
      ]);
    }
  };

  const filterOptionsByProperty = (propertyId) => {
    const propertyIdNum = Number(propertyId);

    const matchesProperty = (row) => Number(row.property_id) === propertyIdNum;

    setFilteredStatuses(statuses.filter(matchesProperty));
    setFilteredRequestTypes(requestTypes.filter(matchesProperty));
    setFilteredLocations(locations.filter(matchesProperty));
    setFilteredRequesters(requesters.filter(matchesProperty));
    setFilteredTechs(techs.filter(matchesProperty));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Handle ID fields for dropdowns
      if (name === 'status') {
        const selectedStatus = filteredStatuses.find(status => status.name === value);
        newData.statusId = selectedStatus ? selectedStatus.id : '';
      } else if (name === 'requestType') {
        const selectedRequestType = filteredRequestTypes.find(type => type.name === value);
        newData.requestTypeId = selectedRequestType ? selectedRequestType.id : '';
      } else if (name === 'location') {
        const selectedLocation = filteredLocations.find(location => location.name === value);
        newData.locationId = selectedLocation ? selectedLocation.id : '';
      }

      return newData;
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    
    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setPreviewImages(previews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create the service request
      const requestData = {
        title: `${formData.requestType} - ${formData.location}`,
        status: formData.status,
        // Add other fields as needed for your backend
        requestType: formData.requestType,
        requestTypeId: formData.requestTypeId,
        location: formData.location,
        locationId: formData.locationId,
        statusId: formData.statusId,
        requesterId: formData.requester,
        problem: formData.problem,
        date: formData.date,
        permission: formData.permission,
        occupied: formData.occupied,
        priority: formData.priority,
        tech: formData.tech,
        notes: formData.notes,
        timeSpent: formData.timeSpent,
        dateFinished: formData.dateFinished,
        followUp: formData.followUp,
        property: formData.property
      };

      const response = await axios.post('/api/requests', requestData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Upload images if any
      if (images.length > 0) {
        const formDataImages = new FormData();
        images.forEach(image => {
          formDataImages.append('images', image);
        });

        await axios.post(`/api/requests/${response.data.id}/images`, formDataImages, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      navigate(returnPath || ROUTES.SERVICE_REQUESTS);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create service request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 app-shell-page">
      <div className="row">
        <div className="col-12">
          <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <h1 className="h3 mb-1">Create Service Request</h1>
              <p className="text-muted small mb-0">Fill in details below; property drives dropdown options.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate(returnPath || ROUTES.SERVICE_REQUESTS)}
              className="btn btn-outline-secondary btn-sm flex-shrink-0"
            >
              {returnPath ? 'Back to inspection' : 'Back to Dashboard'}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Request Details</h5>
                
                {/* Property Selection - Moved to top */}
                <div className="mb-3">
                  <label className="form-label">Property *</label>
                  <select 
                    name="property" 
                    value={formData.property} 
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select Property</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">Select a property to filter available options below</div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select 
                    name="status" 
                    value={formData.status} 
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!formData.property}
                  >
                    <option value="open">Open</option>
                    {filteredStatuses.map(status => (
                      <option key={status.id} value={status.name}>{status.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Request Type *</label>
                  <select 
                    name="requestType" 
                    value={formData.requestType} 
                    onChange={handleInputChange}
                    className="form-select"
                    required
                    disabled={!formData.property}
                  >
                    <option value="">Select Request Type</option>
                    {filteredRequestTypes.map(type => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Location *</label>
                  <select 
                    name="location" 
                    value={formData.location} 
                    onChange={handleInputChange}
                    className="form-select"
                    required
                    disabled={!formData.property}
                  >
                    <option value="">Select Location</option>
                    {filteredLocations.map(location => (
                      <option key={location.id} value={location.name}>{location.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Requester</label>
                  <select 
                    name="requester" 
                    value={formData.requester} 
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!formData.property}
                  >
                    <option value={user?.id || ''}>{user?.name || 'Current User'}</option>
                    {filteredRequesters.map(requester => (
                      <option key={requester.id} value={requester.id}>{requester.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input 
                    type="date" 
                    name="date" 
                    value={formData.date} 
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Priority</label>
                  <select 
                    name="priority" 
                    value={formData.priority} 
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    {priorityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Tech</label>
                  <select 
                    name="tech" 
                    value={formData.tech} 
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!formData.property}
                  >
                    <option value="">Select Tech</option>
                    {filteredTechs.map(tech => (
                      <option key={tech.id} value={tech.id}>{tech.technician}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Problem Description</h5>
                <div className="mb-3">
                  <label className="form-label">Problem</label>
                  <textarea 
                    name="problem" 
                    value={formData.problem} 
                    onChange={handleInputChange}
                    className="form-control"
                    rows="4"
                    required
                    placeholder="Describe the problem..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea 
                    name="notes" 
                    value={formData.notes} 
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Follow Up</label>
                  <textarea 
                    name="followUp" 
                    value={formData.followUp} 
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Follow up actions..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Additional Information</h5>
                
                <div className="mb-3">
                  <div className="form-check">
                    <input 
                      type="checkbox" 
                      name="permission" 
                      checked={formData.permission} 
                      onChange={handleInputChange}
                      className="form-check-input"
                    />
                    <label className="form-check-label">Permission Granted</label>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input 
                      type="checkbox" 
                      name="occupied" 
                      checked={formData.occupied} 
                      onChange={handleInputChange}
                      className="form-check-input"
                    />
                    <label className="form-check-label">Space Occupied</label>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Time Spent</label>
                  <input 
                    type="time" 
                    name="timeSpent" 
                    value={formData.timeSpent} 
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Date Finished</label>
                  <input 
                    type="date" 
                    name="dateFinished" 
                    value={formData.dateFinished} 
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Photo Upload</h5>
                <div className="mb-3">
                  <label className="form-label">Upload Images</label>
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageChange}
                    className="form-control"
                  />
                  <div className="form-text">You can select multiple images from your device or take photos with your camera</div>
                </div>

                {previewImages.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Image Previews</label>
                    <div className="row">
                      {previewImages.map((preview, index) => (
                        <div key={index} className="col-md-3 mb-2">
                          <img 
                            src={preview} 
                            alt={`Preview ${index + 1}`} 
                            className="img-thumbnail"
                            style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-3">
            {error}
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-end gap-2">
              <button 
                type="button" 
                onClick={() => navigate(returnPath || ROUTES.SERVICE_REQUESTS)} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Service Request'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateRequest; 